import React, { useState, useEffect, useRef, Fragment } from "react";

import { Helmet } from "react-helmet";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ImArrowDown } from "react-icons/im";
import Papa from "papaparse";
import templateFile from "../public/FIFO_Calculator_Template.csv";
import resultsFile from "../public/templateResults.csv";
import columnMappingsImg from "../public/columnMappings.png";
import resultsImg from "../public/resultsImg.png";
import warningsImg from "../public/warnings.png";

import "./HowItWorks.css";

const HowItWorks = () => {
  const [data, setData] = useState([]);
  const [resultsData, setResultsData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [resultsHeaders, setResultsHeaders] = useState([]);
  const containerRef = useRef(null);
  const resultsRef = useRef(null);
  const [shadows, setShadows] = useState({
    top: false,
    bottom: false,
  });
  const [resultsShadows, setResultsShadows] = useState({
    top: false,
    bottom: false,
  });

  const updateShadows = () => {
    const container = containerRef.current;
    const rContainer = resultsRef.current;

    if (container) {
      const isScrolledToTop = container.scrollTop > 0;
      const isScrolledToBottom =
        container.scrollTop + container.clientHeight < container.scrollHeight;

      setShadows({
        top: isScrolledToTop,
        bottom: isScrolledToBottom,
      });
    }

    if (rContainer) {
      const isScrolledToTop = rContainer.scrollTop > 0;
      const isScrolledToBottom =
        rContainer.scrollTop + rContainer.clientHeight <
        rContainer.scrollHeight;

      setResultsShadows({
        top: isScrolledToTop,
        bottom: isScrolledToBottom,
      });
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    const rContainer = resultsRef.current;

    if (container) {
      updateShadows(); // Ensure shadows update on mount and when dependencies change
    }
    if (rContainer) {
      updateShadows(); // Ensure shadows update on mount and when dependencies change
    }
  }, [data, resultsData]);

  useEffect(() => {
    const container = containerRef.current;
    const rContainer = resultsRef.current;

    if (container) {
      container.addEventListener("scroll", updateShadows);
      updateShadows();
    }
    if (rContainer) {
      rContainer.addEventListener("scroll", updateShadows);
      updateShadows();
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", updateShadows);
      }
      if (rContainer) {
        rContainer.removeEventListener("scroll", updateShadows);
      }
    };
  }, []);

  useEffect(() => {
    fetch(templateFile)
      .then((response) => response.text())
      .then((text) => {
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            setHeaders(Object.keys(result.data[0]));
            setData(result.data);
          },
        });
      });
    fetch(resultsFile)
      .then((response) => response.text())
      .then((text) => {
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            setResultsHeaders(Object.keys(result.data[0]));
            setResultsData(result.data);
          },
        });
      });
  }, []);

  return (
    <div className="about-us-container">
      <Helmet>
        <title>Coin Basis: How It Works</title>
        <meta
          property="og:title"
          content="About-Us - Far Flung Determined Jaguar"
        />
        {/* Google Analytics */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-1T6SHZQ9X8"></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-1T6SHZQ9X8');
          `}
        </script>
        <script>
          {`
            (function(w,d,s,l,i){
              w[l]=w[l]||[];
              w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
              var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
              j.async=true;
              j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
              f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-MTZT7D3V');
          `}
        </script>
      </Helmet>
      <Navbar />
      <div className="hero8-header26 thq-section-padding">
        <div className="hero8-max-width thq-flex-column thq-section-max-width">
          <div className="hero8-column">
            <div className="hero8-content">
              <div className="hero8-actions"></div>
            </div>
            <div className="hero-container">
            <h1 className="hero8-text10 thq-heading-1">
              <Fragment>
                <span className="hero8-text25">How It Works</span>
              </Fragment>
            </h1>
            </div>
            <h6 className="hero8-text10">
              <Fragment>
                <span className="hero8-text25 thq-heading-3">
                  Coin Basis&reg; uses the FIFO (First In, First Out) method to
                  calculate the cost basis of your cryptocurrency transactions,
                  which is the IRS's standard method. Our algorithm analyzes
                  your transaction data to determine the taxable events and
                  outputs them in a detailed CSV report. Here's how you can get
                  yours in under a minute:
                </span>
              </Fragment>
            </h6>
            <ImArrowDown size={48} className="down-arrow" />
            <div
              style={{
                width: "100%",
                alignSelf: "flex-start",
                alignItems: "flex-start",
              }}
            >
              <Fragment>
                <span className="hero8-text25 thq-heading-4">
                  1. Supply Coin Basis&reg; with your transaction history file
                  in Comma Separated Value (CSV) format
                </span>
              </Fragment>
              <br />
              <Fragment>
                <span className="hero8-text25 thq-body-large">
                  Download our template file shown below{" "}
                  <a href={templateFile} download="coinbasis_template.csv">
                    here
                  </a>
                </span>
              </Fragment>
              <div
                ref={containerRef}
                className={`scrollable-container ${
                  shadows.top && shadows.bottom
                    ? "both-shadows"
                    : shadows.top
                    ? "top-shadow"
                    : shadows.bottom
                    ? "bottom-shadow"
                    : ""
                }`}
              >
                {data.length > 0 ? (
                  <table
                    border="1"
                    style={{
                      width: "max-content",
                      textAlign: "left",
                    }}
                  >
                    <thead>
                      <tr>
                        {headers.map((header, index) => (
                          <th key={index}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {headers.map((header, colIndex) => (
                            <td key={colIndex}>{row[header]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>Loading Sample data...</p>
                )}
              </div>
            </div>
            <ImArrowDown size={48} className="down-arrow" />
            <div
              style={{
                width: "100%",
                alignSelf: "flex-start",
                alignItems: "flex-start",
              }}
            >
              <Fragment>
                <span className="hero8-text25 thq-heading-4">
                  2. Update the mappings so that our software can identify how
                  you track your data
                </span>
              </Fragment>
              <img
                alt="ColumnMappings"
                src={columnMappingsImg}
                height={300}
                style={{
                  display: "block",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              />
            </div>
            <ImArrowDown size={48} className="down-arrow" />
            <div
              style={{
                width: "100%",
                alignSelf: "flex-start",
                alignItems: "flex-start",
              }}
            >
              <Fragment>
                <span className="hero8-text25 thq-heading-4">
                  3. Generate your estimates and wait a few seconds for the
                  results!
                </span>
              </Fragment>
              <img
                alt="Results"
                src={resultsImg}
                height={300}
                style={{
                  display: "block",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              />
            </div>
            <ImArrowDown size={48} className="down-arrow" />
            <div
              style={{
                width: "100%",
                alignSelf: "flex-start",
                alignItems: "flex-start",
              }}
            >
              <Fragment>
                <span className="hero8-text25 thq-heading-4">
                  4. Review warning messages and results to identify any
                  oddities, then fix any underlying data problems and try again
                </span>
              </Fragment>
              <img
                alt="Warnings"
                src={warningsImg}
                width={"100%"}
                style={{
                  display: "block",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              />
            </div>
            <ImArrowDown size={48} className="down-arrow" />
            <div
              style={{
                width: "100%",
                alignSelf: "flex-start",
                alignItems: "flex-start",
              }}
            >
              <Fragment>
                <span className="hero8-text25 thq-heading-4">
                  5. Review the output files to see tax year transaction
                  history! This is what the main summary report looks like.
                </span>
              </Fragment>
              <div
                ref={resultsRef}
                className={`scrollable-container ${
                  resultsShadows.top && resultsShadows.bottom
                    ? "both-shadows"
                    : resultsShadows.top
                    ? "top-shadow"
                    : resultsShadows.bottom
                    ? "bottom-shadow"
                    : ""
                }`}
              >
                {resultsData.length > 0 ? (
                  <table
                    border="1"
                    style={{
                      marginTop: "20px",
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    <thead>
                      <tr>
                        {resultsHeaders.map((header, index) => (
                          <th key={index}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {resultsData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {resultsHeaders.map((header, colIndex) => (
                            <td key={colIndex}>{row[header]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>Loading Results data...</p>
                )}
              </div>
            </div>
            <Fragment>
              <span className="hero8-text25 thq-heading-4">
                You will also receive an individual income report and a capital
                gains report which can be used while filing!
              </span>
            </Fragment>
            <Fragment>
              <span className="hero8-text25 thq-heading-4">Happy Filing!</span>
            </Fragment>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HowItWorks;
